import React, { Fragment, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import Detached from './Detached';

interface Props {
  opened: boolean;
  className?: string;
  containerClassName?: string;
  blackBackdrop?: boolean;
  primary?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  detached?: boolean | string;

  onClose?(): unknown;
  onOpen?(): unknown;
}

interface ContainerProps {
  opened: boolean;
  detached?: boolean | string;
  className?: string;
  children: React.ReactNode;
}

const bodyClassName = 'body-modal--open';

const Container = ({ className, detached, opened, children }: ContainerProps) => {
  useEffect(() => {
    if (!detached) return;
    if (opened) {
      document.body.classList.add(bodyClassName);
    } else {
      document.body.classList.remove(bodyClassName);
    }
  }, [opened, detached]);

  let Wrap: React.ElementType = Fragment;
  const props: { container?: string } = {};
  if (detached) {
    Wrap = Detached;
    props.container = typeof detached === 'string' ? detached : 'modal';
  }
  return (
    <Wrap {...props}>
      <div className={className}>{children}</div>
    </Wrap>
  );
};

export default function MobileModal({ opened, size, ...props }: React.PropsWithChildren<Props>) {
  const [closing, setClosing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    setClosing(true);
    setTimeout(() => {
      props.onClose();
    }, 500);
  };

  useEffect(() => {
    if (!opened || !ref.current) return;
    ref.current.scrollTop = 0;
  }, [opened]);

  useEffect(() => {
    if (!opened) return;
    props.onOpen?.();
  }, [opened, props.onOpen]);

  return (
    <Container
      className={cn(
        'mobile-modal',
        {
          primary: props.primary === true && !props.blackBackdrop,
          opened,
          closing,
        },
        size ? `size-${size}` : '',
        props.className,
      )}
      detached={props.detached}
      opened={opened}
    >
      <div className="mobile-modal-overflow">
        <div className="mobile-modal-inner">
          <div className="mobile-modal-header">
            {props.onClose ? <i className="icon icon-close" onClick={handleClose} /> : null}
          </div>
          <div className={cn('mobile-modal-body', props.containerClassName)} ref={ref}>
            {props.children}
          </div>
        </div>
      </div>
      <div className="mobile-modal-backdrop" onClick={handleClose} />
    </Container>
  );
}
